import { APP_BOOTSTRAP_LISTENER, PLATFORM_ID, Provider, inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { NavigationError, Router } from '@angular/router';
import { filter } from 'rxjs/operators';

/**
 * Фолбэк на полную загрузку, когда ленивый модуль маршрута не скачался.
 *
 * Роуты с `loadChildren` (купон, линия, блоги…) тянут свой чанк с CDN только в
 * момент внутренней навигации. Если CDN у конкретного пользователя недоступен
 * (DNS-фильтр блокировщика, антивирус, провайдер), `main.js` у него ещё лежит в
 * кэше на год, а свежий хэшированный чанк не приходит: навигация падает с
 * NavigationError, и клик по вкладке «ничего не делает». При этом полная загрузка
 * той же страницы работает — её рисует SSR. Поэтому при такой ошибке уходим на
 * целевой URL обычным переходом браузера.
 *
 * Защита от петли: на первичной навигации (страница только что загрузилась,
 * SSR-разметка уже на экране) перезагрузку не делаем — иначе при полностью
 * недоступном CDN страница перезагружалась бы бесконечно. Второй страховкой
 * служит отметка в sessionStorage: один и тот же URL перезагружаем не более
 * одного раза за сессию вкладки.
 */
const STORAGE_KEY = 'ovb-chunk-fallback';

export function isChunkLoadError(error: any): boolean {
    if (!error) {
        return false;
    }
    if (error.name === 'ChunkLoadError') {
        return true;
    }
    const message = String(error.message ?? error);
    return /dynamically imported module|Importing a module script failed|Loading chunk|ChunkLoadError/i.test(message);
}

export function provideChunkLoadFallback(): Provider {
    return {
        provide: APP_BOOTSTRAP_LISTENER,
        multi: true,
        useFactory: () => {
            const platformId = inject(PLATFORM_ID);
            const router = inject(Router);
            return () => {
                if (!isPlatformBrowser(platformId)) {
                    return;
                }
                router.events
                    .pipe(filter((event): event is NavigationError => event instanceof NavigationError))
                    .subscribe((event) => {
                        if (!isChunkLoadError(event.error)) {
                            return;
                        }
                        const url = event.url;
                        // eslint-disable-next-line no-console
                        console.error('[chunk-fallback] не удалось загрузить модуль страницы', url, event.error);
                        if (event.id === 1) {
                            // первичная навигация: SSR-разметка уже на экране, перезагрузка не поможет
                            return;
                        }
                        let previous: string | null = null;
                        try {
                            previous = sessionStorage.getItem(STORAGE_KEY);
                        } catch (e) {
                            // приватный режим / запрет storage — работаем без отметки
                        }
                        if (previous === url) {
                            return;
                        }
                        try {
                            sessionStorage.setItem(STORAGE_KEY, url);
                        } catch (e) {
                        }
                        // eslint-disable-next-line no-console
                        console.warn('[chunk-fallback] переход полной загрузкой', url);
                        globalThis.location?.assign(url);
                    });
            };
        },
    };
}
